import React from "react";
import { Link } from "react-router-dom";
import { LegalLayout } from "../components/LegalLayout";
import {
  APP_NAME, SUPPORT_EMAIL, LEGAL_ROUTES, LEGAL_INFO,
} from "../lib/legal";

// Section helper — H2 anchor + spacing.
function Section({ id, title, children }) {
  return (
    <section id={id} style={{ marginTop: 28 }}>
      <h2 style={{
        margin: "0 0 10px",
        fontSize: 20,
        fontWeight: 700,
        color: "var(--mf-text)",
        letterSpacing: -0.2,
        lineHeight: 1.3,
      }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function P({ children, style }) {
  return <p style={{ margin: "0 0 12px", ...style }}>{children}</p>;
}

function UL({ children }) {
  return <ul style={{ margin: "0 0 12px", paddingLeft: 22 }}>{children}</ul>;
}

// Public privacy policy — content derived from the real audit of the codebase.
// No promise the app cannot honour. Wording validated by the editor.
export default function Privacy() {
  return (
    <LegalLayout
      title="Politique de confidentialité"
      description={`Comment ${APP_NAME} collecte, utilise et protège vos données personnelles.`}
      pathname={LEGAL_ROUTES.privacy}
    >
      <P style={{ color: "var(--mf-text-secondary)", fontSize: 15 }}>
        Cette politique explique en langage clair quelles données {APP_NAME} traite,
        pourquoi, et comment vous en gardez le contrôle. Elle décrit uniquement le
        fonctionnement réel de l&apos;application, sans promesse technique non vérifiée.
      </P>

      {/* Sommaire */}
      <nav aria-label="Sommaire" style={{
        background: "var(--mf-bg-card)",
        border: "1px solid var(--mf-divider)",
        borderRadius: 12,
        padding: "12px 16px",
        margin: "12px 0 24px",
        fontSize: 14,
      }}>
        <strong style={{ display: "block", marginBottom: 6, fontSize: 13, color: "var(--mf-text-secondary)", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Sommaire
        </strong>
        <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.9 }}>
          <li><a href="#responsable">Responsable de traitement</a></li>
          <li><a href="#donnees">Données que nous traitons</a></li>
          <li><a href="#finalites">Finalités et bases juridiques</a></li>
          <li><a href="#tiers">Services tiers réellement utilisés</a></li>
          <li><a href="#scan">Analyse automatisée des factures</a></li>
          <li><a href="#partages">Liens de partage publics</a></li>
          <li><a href="#push">Notifications Web Push</a></li>
          <li><a href="#stockages">Stockages du navigateur et cookies</a></li>
          <li><a href="#hebergement">Hébergement et infrastructure</a></li>
          <li><a href="#transferts">Transferts de données hors de l&apos;Espace économique européen</a></li>
          <li><a href="#duree">Durées de conservation</a></li>
          <li><a href="#securite">Sécurité</a></li>
          <li><a href="#droits">Vos droits</a></li>
          <li><a href="#suppression">Suppression de votre compte</a></li>
          <li><a href="#mineurs">Mineurs</a></li>
          <li><a href="#modifications">Modifications de cette politique</a></li>
          <li><a href="#contact">Contact</a></li>
        </ol>
      </nav>

      <Section id="responsable" title="1. Responsable de traitement">
        <P>
          L&apos;éditeur de {APP_NAME} est le responsable du traitement de vos
          données personnelles au sens du RGPD. Les informations relatives à
          l&apos;éditeur figurent sur la page{" "}
          <Link to={LEGAL_ROUTES.legal}>Mentions légales</Link>. Pour toute
          question relative à vos données, écrivez à{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </P>
      </Section>

      <Section id="donnees" title="2. Données que nous traitons">
        <P>Deux catégories principales.</P>
        <P><strong>Données que vous nous fournissez :</strong></P>
        <UL>
          <li>Votre email et, si vous le renseignez, votre nom.</li>
          <li>Votre mot de passe pour les comptes créés par email — stocké uniquement sous forme d&apos;empreinte cryptographique (bcrypt), jamais en clair.</li>
          <li>Les informations de vos véhicules : marque, modèle, immatriculation, année, kilométrage, type de carburant, éventuelle photo, prix et date d&apos;achat si vous les indiquez.</li>
          <li>Vos entretiens, interventions, entretiens personnalisés, kilométrages successifs.</li>
          <li>Les documents et pièces jointes que vous téléversez (factures, cartes grises, assurances, contrôles techniques, macarons VTC, autres documents administratifs, images).</li>
          <li>Les notes libres que vous saisissez — elles peuvent contenir des informations personnelles selon ce que vous y écrivez.</li>
        </UL>
        <P style={{ marginTop: 8 }}>
          <strong>Rappels importants concernant les documents téléversés :</strong>
        </P>
        <UL>
          <li>Ne téléversez que les documents utiles à la gestion de vos véhicules.</li>
          <li>Vous devez disposer du droit de téléverser ces documents.</li>
          <li>Il est recommandé de masquer les informations inutiles avant l&apos;envoi.</li>
          <li>{APP_NAME} n&apos;a pas vocation à recevoir des données de santé, des données bancaires complètes, des mots de passe d&apos;autres services, ni des documents sans lien avec vos véhicules.</li>
          <li>Les documents téléversés ne sont pas anonymisés.</li>
        </UL>
        <P style={{ marginTop: 8 }}><strong>Données générées par l&apos;utilisation :</strong></P>
        <UL>
          <li>Un identifiant interne unique associé à votre compte.</li>
          <li>Vos préférences de notification.</li>
          <li>Un journal d&apos;activité interne (créations, mises à jour, partages, événements techniques). Ce journal est purgé automatiquement au bout de 90 jours.</li>
          <li>Les brouillons temporaires de scan de facture, conservés au maximum 30 jours.</li>
          <li>Si vous utilisez la connexion Google : votre identifiant Google, votre email Google, votre nom, et l&apos;URL de votre photo de profil, transmis par Google.</li>
          <li>Si vous activez les notifications Web Push : les informations techniques de votre abonnement au service Push de votre navigateur (endpoint, clés cryptographiques, chaîne du navigateur).</li>
          <li>Si vous demandez une réinitialisation de mot de passe : une empreinte cryptographique tronquée de votre adresse IP, jamais l&apos;adresse elle-même.</li>
        </UL>
        <P>
          Certaines informations peuvent contenir des données personnelles si vous
          choisissez de les ajouter (par exemple dans une note libre ou dans un
          document téléversé). Vous en restez responsable.
        </P>
      </Section>

      <Section id="finalites" title="3. Finalités et bases juridiques">
        <P>Nous utilisons vos données uniquement pour :</P>
        <UL>
          <li>Créer votre compte et vous authentifier (exécution du service).</li>
          <li>Afficher, organiser et gérer vos véhicules, documents et interventions (exécution du service).</li>
          <li>Vous envoyer des alertes et notifications sur les échéances que vous suivez (exécution du service).</li>
          <li>Permettre l&apos;analyse automatisée des factures que vous choisissez de scanner (exécution du service).</li>
          <li>Vous permettre de partager volontairement des documents via un lien (exécution du service, sur votre initiative).</li>
          <li>Assurer la sécurité, prévenir les abus et diagnostiquer les erreurs techniques (intérêt légitime).</li>
          <li>Répondre à vos demandes de support (exécution de mesures précontractuelles ou intérêt légitime).</li>
          <li>Vous permettre de supprimer votre compte et vos données (exécution du service et droits RGPD).</li>
        </UL>
        <P>
          Le consentement s&apos;applique uniquement aux traitements qui l&apos;exigent :
          activation de l&apos;appareil photo pour scanner un document, activation
          des notifications Web Push, envoi d&apos;éventuels emails non
          transactionnels dans le futur.
        </P>
        <P>
          Nous n&apos;utilisons pas vos données pour de la publicité, du profilage
          commercial, ni pour de la revente à des tiers.
        </P>
      </Section>

      <Section id="tiers" title="4. Services tiers réellement utilisés">
        <P>
          Le fonctionnement de {APP_NAME} repose sur un petit nombre de prestataires
          techniques. Voici l&apos;état réel, avec ce qui est actif ou non aujourd&apos;hui.
        </P>
        <UL>
          <li>
            <strong>Emergent — hébergement et infrastructure — actif.</strong>{" "}
            {APP_NAME} est hébergée sur l&apos;infrastructure Emergent (backend,
            base de données, authentification, notifications, exécution
            technique). Les traitements techniques peuvent être réalisés aux
            États-Unis et en Inde. Voir la section{" "}
            <a href="#transferts">Transferts hors EEE</a>.
          </li>
          <li>
            <strong>Emergent (authentification Google) — actif si vous utilisez ce mode de connexion.</strong>{" "}
            Un identifiant de session est échangé avec la plateforme Emergent,
            qui nous retourne votre email Google, votre nom et votre identifiant
            Google. Nous n&apos;avons jamais accès à votre mot de passe Google.
          </li>
          <li>
            <strong>Emergent (routage vers un fournisseur d&apos;intelligence artificielle) — actif lorsque vous utilisez le scan de facture.</strong>{" "}
            La plateforme technique Emergent peut acheminer l&apos;analyse d&apos;une
            facture vers un fournisseur de modèle d&apos;intelligence artificielle
            (les familles de modèles concernées peuvent notamment provenir
            d&apos;OpenAI, d&apos;Anthropic ou de Google). Le fournisseur exact peut
            dépendre du routage technique de la plateforme. Selon les
            informations communiquées par Emergent, les fournisseurs d&apos;IA
            associés à cette infrastructure ne sont pas autorisés à utiliser
            les informations traitées pour développer, améliorer ou entraîner
            leurs technologies pour d&apos;autres clients. Voir la section{" "}
            <a href="#scan">Analyse automatisée des factures</a>.
          </li>
          <li>
            <strong>Script technique Emergent (assets.emergent.sh) — actif.</strong>{" "}
            Un petit script technique fourni par Emergent est chargé lors de
            l&apos;ouverture de l&apos;application. Il intercepte certains événements
            techniques (erreurs de la console du navigateur, erreurs de
            construction, échecs de requêtes réseau) afin d&apos;alimenter la
            supervision technique de la plateforme d&apos;hébergement. Il ne
            s&apos;agit pas d&apos;un outil publicitaire, ni d&apos;un outil de suivi
            marketing. Nous ne l&apos;utilisons pas pour du profilage commercial.
          </li>
          <li>
            <strong>Resend — activé pour l&apos;envoi d&apos;emails transactionnels lorsqu&apos;il est configuré.</strong>{" "}
            Utilisé uniquement pour envoyer les emails techniques essentiels
            (par exemple la réinitialisation de mot de passe). Aucun email
            marketing n&apos;est envoyé.
          </li>
          <li>
            <strong>Services Push standards des navigateurs — actifs si vous activez les notifications.</strong>{" "}
            Les notifications sont acheminées via les services Push standards
            de votre navigateur (par exemple les services Push de Google
            Chrome, Mozilla Firefox ou Apple Safari selon votre navigateur).
            Ces services voient un identifiant technique d&apos;abonnement, mais
            le contenu des notifications est chiffré grâce au protocole VAPID.
          </li>
          <li>
            <strong>Google Fonts — actif.</strong> {APP_NAME} charge la police
            Manrope depuis les serveurs de Google. Cela peut transmettre à
            Google des informations techniques standards (adresse IP publique,
            en-têtes du navigateur) au moment du chargement de la police.
          </li>
        </UL>
        <P>
          Lorsque vous choisissez une photo depuis l&apos;application de galerie
          de votre appareil (par exemple Google Photos, la Galerie Samsung ou
          la Photothèque Apple), cette application vous sert uniquement de
          sélecteur local. {APP_NAME} ne transmet pas automatiquement vos
          fichiers à ces applications : c&apos;est vous qui choisissez le fichier
          à envoyer à {APP_NAME}.
        </P>
      </Section>

      <Section id="scan" title="5. Analyse automatisée des factures">
        <P>
          Le scan de facture est facultatif. Il fonctionne ainsi :
        </P>
        <UL>
          <li>Vous choisissez une image de facture depuis votre appareil, ou vous la prenez avec l&apos;appareil photo.</li>
          <li>L&apos;image est transmise au serveur de {APP_NAME}.</li>
          <li>La plateforme technique Emergent peut acheminer l&apos;analyse vers un fournisseur de modèle d&apos;intelligence artificielle. Les familles de modèles concernées peuvent notamment provenir d&apos;OpenAI, d&apos;Anthropic ou de Google. Le fournisseur exact peut dépendre du routage technique de la plateforme.</li>
          <li>Le modèle extrait des informations structurées : la date, le fournisseur, le montant total, le kilométrage indiqué, la catégorie et éventuellement les lignes de la facture.</li>
          <li>Le résultat vous est proposé et doit toujours être vérifié : il peut contenir des erreurs. L&apos;analyse ne remplace pas une vérification humaine.</li>
          <li>Un brouillon non confirmé est conservé pendant 30 jours maximum, puis automatiquement supprimé.</li>
          <li>Si vous confirmez, la facture peut être conservée comme pièce jointe de l&apos;intervention.</li>
        </UL>
        <P>
          Vous vous engagez à ne scanner que des documents que vous êtes
          autorisé·e à utiliser.
        </P>
      </Section>

      <Section id="partages" title="6. Liens de partage publics">
        <P>
          Vous pouvez volontairement créer un lien pour partager certains documents
          d&apos;un véhicule (par exemple avec un garagiste ou un futur acheteur).
        </P>
        <UL>
          <li>Le lien peut afficher la marque, le modèle, l&apos;année et l&apos;immatriculation du véhicule concerné.</li>
          <li>Les documents que vous avez sélectionnés sont visibles.</li>
          <li>Le téléchargement des fichiers dépend du réglage que vous choisissez au moment de la création.</li>
          <li><strong>Le lien n&apos;est pas protégé par un mot de passe.</strong> Toute personne disposant du lien peut y accéder tant qu&apos;il n&apos;est ni révoqué ni expiré.</li>
          <li>Vous pouvez révoquer le lien à tout moment ou lui donner une date d&apos;expiration.</li>
          <li>Tous vos liens de partage deviennent invalides lors de la suppression de votre compte.</li>
        </UL>
      </Section>

      <Section id="push" title="7. Notifications Web Push">
        <P>
          Les notifications sont facultatives. Elles nécessitent une autorisation
          explicite de votre navigateur ou de votre système. Vous pouvez les
          désactiver à tout moment depuis votre navigateur ou depuis votre profil.
          Un identifiant technique d&apos;abonnement est conservé le temps que vous
          restez abonné·e ; il est supprimé lors de la suppression de votre compte.
        </P>
      </Section>

      <Section id="stockages" title="8. Stockages du navigateur et cookies">
        <P>
          {APP_NAME} n&apos;utilise <strong>aucun cookie applicatif</strong>, aucun
          cookie publicitaire et aucun outil analytique de mesure d&apos;audience.
          Aucune bannière de consentement n&apos;est nécessaire car nous
          n&apos;utilisons que des stockages strictement nécessaires au
          fonctionnement.
        </P>
        <P>Voici précisément ce qui est stocké dans votre navigateur :</P>
        <P><strong>Stockage permanent (localStorage) :</strong></P>
        <UL>
          <li>Un jeton d&apos;authentification qui vous permet de rester connecté·e.</li>
          <li>Un cache léger de votre profil (identifiant, email, nom) pour un affichage plus rapide.</li>
          <li>Un indicateur qui mémorise que vous avez vu l&apos;écran de bienvenue, pour ne plus vous le montrer.</li>
          <li>Votre préférence de tri sur la liste des véhicules.</li>
        </UL>
        <P><strong>Stockage temporaire de session (sessionStorage) :</strong></P>
        <UL>
          <li>Deux valeurs techniques utilisées uniquement pour la réauthentification Google lorsque vous supprimez votre compte. Elles sont automatiquement effacées à la fin du processus.</li>
        </UL>
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: "pointer", fontSize: 13, color: "var(--mf-text-secondary)" }}>Détails techniques (noms exacts)</summary>
          <div style={{ marginTop: 8, fontSize: 13, color: "var(--mf-text-secondary)" }}>
            <p><strong>localStorage :</strong> myfleet_token, myfleet_user, myfleet_welcome_seen, myfleet_vehicles_sort.</p>
            <p><strong>sessionStorage :</strong> mf_delete_reauth, mf_delete_google_session_id.</p>
          </div>
        </details>
      </Section>

      <Section id="hebergement" title="9. Hébergement et infrastructure">
        <P>{LEGAL_INFO.hostStatement}</P>
        <P>
          Les données applicatives sont stockées dans une infrastructure de
          base de données MongoDB configurée et exploitée dans l&apos;environnement
          technique du service. La localisation et la chaîne contractuelle
          exactes dépendent de l&apos;infrastructure Emergent et restent en cours
          de confirmation. Nous ne prétendons pas ce que nous ne pouvons pas
          prouver.
        </P>
        <P>
          Documents publics du fournisseur d&apos;hébergement :{" "}
          <a href={LEGAL_INFO.hostTermsUrl} target="_blank" rel="noreferrer noopener">
            Conditions d&apos;utilisation Emergent
          </a>{" "}·{" "}
          <a href={LEGAL_INFO.hostPrivacyUrl} target="_blank" rel="noreferrer noopener">
            Politique de confidentialité Emergent
          </a>. Contact relatif à la confidentialité chez ce fournisseur :{" "}
          <a href={`mailto:${LEGAL_INFO.hostPrivacyContact}`}>{LEGAL_INFO.hostPrivacyContact}</a>.
        </P>
      </Section>

      <Section id="transferts" title="10. Transferts de données hors de l'Espace économique européen">
        <P>
          Selon les informations communiquées par le fournisseur d&apos;hébergement,
          certaines données traitées par {APP_NAME} peuvent être transférées
          vers <strong>les États-Unis et l&apos;Inde</strong>. Cela peut concerner
          l&apos;hébergement, la base de données, l&apos;authentification, les
          sauvegardes, les journaux techniques et certains traitements
          d&apos;intelligence artificielle utilisés pour l&apos;analyse des factures.
        </P>
        <P>
          Aucun hébergement exclusivement situé dans l&apos;Espace économique
          européen n&apos;est actuellement disponible pour {APP_NAME}.
        </P>
        <P>
          Il est également porté à votre connaissance que :
        </P>
        <UL>
          <li>Le fournisseur d&apos;hébergement Emergent ne propose actuellement aucun accord de traitement des données (DPA) au sens de l&apos;article 28 du RGPD ; aucune date de disponibilité n&apos;a été annoncée à ce jour.</li>
          <li>Le statut exact des clauses contractuelles types applicables à ces transferts reste en attente de confirmation juridique.</li>
          <li>L&apos;Inde ne bénéficie pas d&apos;une décision générale d&apos;adéquation adoptée par la Commission européenne.</li>
          <li>Ces éléments doivent être présentés en toute transparence : les transferts ne peuvent pas être présentés comme définitivement sécurisés sur le plan juridique.</li>
        </UL>
        <P>
          Les utilisateurs qui souhaitent obtenir des informations complémentaires
          peuvent nous écrire à{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </P>
      </Section>

      <Section id="duree" title="11. Durées de conservation">
        <UL>
          <li>Données de votre compte : tant que le compte existe.</li>
          <li>Données métier (véhicules, documents, interventions) : tant que le compte existe.</li>
          <li>Journal d&apos;activité interne : purgé automatiquement au bout de 90 jours.</li>
          <li>Brouillons de scan de facture non confirmés : jusqu&apos;à 30 jours, puis suppression automatique.</li>
          <li>Jetons de réinitialisation de mot de passe : 30 minutes maximum.</li>
          <li>Abonnements aux notifications Web Push : tant que l&apos;abonnement est actif.</li>
          <li>Liens de partage : jusqu&apos;à leur révocation, leur expiration, ou la suppression de votre compte.</li>
          <li>Logs techniques d&apos;infrastructure et d&apos;hébergement, sauvegardes du prestataire : la durée exacte dépend de la configuration technique du prestataire d&apos;hébergement et reste en cours de confirmation.</li>
        </UL>
        <P>
          Voir la section <a href="#suppression">Suppression de votre compte</a>{" "}
          pour le détail des délais applicables lors de la suppression.
        </P>
      </Section>

      <Section id="securite" title="12. Sécurité">
        <P>
          {APP_NAME} met en œuvre des mesures techniques et organisationnelles
          adaptées aux risques identifiés, sans pouvoir garantir une sécurité
          absolue.
        </P>
        <UL>
          <li>Les mots de passe sont hashés avec l&apos;algorithme bcrypt. Ils ne sont jamais stockés en clair.</li>
          <li>L&apos;accès à l&apos;API est protégé par un jeton d&apos;authentification (JWT) transmis dans les en-têtes de requête.</li>
          <li>Chaque enregistrement est strictement isolé par identifiant de propriétaire.</li>
          <li>L&apos;application est servie en HTTPS dans son environnement hébergé.</li>
          <li>Les jetons de réinitialisation ne sont pas stockés en clair : nous n&apos;en conservons que l&apos;empreinte cryptographique.</li>
          <li>Certaines requêtes sont limitées en fréquence pour prévenir les abus.</li>
          <li>Les fichiers téléversés sont contrôlés en type et en taille.</li>
          <li>La suppression de votre compte est possible à tout moment ; elle entraîne également la révocation de vos abonnements aux notifications.</li>
        </UL>
      </Section>

      <Section id="droits" title="13. Vos droits">
        <P>Conformément au RGPD, vous disposez des droits suivants :</P>
        <UL>
          <li>Droit d&apos;accès à vos données.</li>
          <li>Droit de rectification.</li>
          <li>Droit à l&apos;effacement.</li>
          <li>Droit à la limitation du traitement.</li>
          <li>Droit d&apos;opposition lorsqu&apos;il est applicable.</li>
          <li>Droit à la portabilité lorsqu&apos;il est applicable.</li>
          <li>Droit de retirer votre consentement lorsque le traitement repose dessus.</li>
          <li>Droit de définir des directives concernant le sort des données après le décès, lorsque cela est applicable.</li>
          <li>Droit d&apos;introduire une réclamation auprès de la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noreferrer noopener">www.cnil.fr</a>), autorité de contrôle française compétente.</li>
        </UL>
        <P>Certaines de ces actions sont déjà disponibles directement dans l&apos;application :</P>
        <UL>
          <li>Modifier votre nom depuis votre profil.</li>
          <li>Activer ou désactiver le récapitulatif email et les notifications push.</li>
          <li>Révoquer vos liens de partage.</li>
          <li>Supprimer votre compte depuis votre profil.</li>
        </UL>
        <P>
          Pour les autres demandes, écrivez à{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Une
          vérification raisonnable de votre identité pourra vous être demandée
          — aucune copie excessive de pièce d&apos;identité n&apos;est exigée. Les
          demandes seront traitées dans les délais prévus par la
          réglementation applicable.
        </P>
      </Section>

      <Section id="suppression" title="14. Suppression de votre compte">
        <P>
          Vous pouvez supprimer votre compte à tout moment depuis votre profil.
          La procédure et les catégories de données concernées sont détaillées
          sur la page dédiée <Link to={LEGAL_ROUTES.deleteAccount}>Suppression de compte</Link>.
        </P>
        <P>
          La suppression du compte entraîne la suppression ou la désactivation
          rapide de l&apos;accès au compte et la suppression des données actives
          associées selon le fonctionnement technique du service. Certaines
          copies résiduelles peuvent toutefois subsister temporairement dans
          les sauvegardes, journaux techniques ou systèmes de reprise. Selon
          les informations communiquées par Emergent, la suppression
          permanente des données personnelles intervient au plus tard{" "}
          <strong>45 jours</strong> après confirmation de la demande, sauf
          obligation légale, nécessité de sécurité, prévention de la fraude
          ou défense de droits en justice.
        </P>
      </Section>

      <Section id="mineurs" title="15. Mineurs">
        <P>
          {APP_NAME} est destiné aux utilisateurs âgés d&apos;au moins <strong>15 ans</strong>.
          Lorsqu&apos;une autorisation du représentant légal est nécessaire,
          l&apos;utilisation doit être effectuée avec son accord et sous sa
          responsabilité. {APP_NAME} ne cherche pas volontairement à collecter
          les données d&apos;enfants de moins de 15 ans.
        </P>
      </Section>

      <Section id="modifications" title="16. Modifications de cette politique">
        <P>
          Cette politique peut évoluer, notamment lorsque les informations
          juridiques complètes du fournisseur d&apos;hébergement seront confirmées,
          lors de nouvelles fonctionnalités, ou pour améliorer la clarté du
          texte. La date d&apos;entrée en vigueur et la date de dernière mise à
          jour figurent en haut de la page.
        </P>
      </Section>

      <Section id="contact" title="17. Contact">
        <P>
          Pour toute question relative à vos données, écrivez à{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Ce contact
          est également le point d&apos;entrée pour toutes les demandes RGPD.
        </P>
      </Section>
    </LegalLayout>
  );
}
